import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { EMPTY_PERMISSIONS, PermissionKey, User, UserRole } from "../models/User";
import { isContactInfoStale } from "./contactInfoPolicy";
import { ROLE_IDS } from "../types/roles";
import getMe from "../api/UsersApi";
import getRole from "../api/RolesApi";
import { logout as logoutApi } from "../api/AuthApi";

export const AUTH_STORAGE_KEY = "sistema-entrevistas:user";

type AuthContextValue = {
  user: User | undefined;
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>;
  isAuthLoading: boolean;
  logout: () => Promise<void>;
  hasPermission: (key: PermissionKey) => boolean;
  contactInfoStale: boolean;
};

const validRoles: UserRole[] = ROLE_IDS;

export function normalizeRole(role: unknown): UserRole | null {
  if (role === "auxiliar") {
    return "assistente";
  }

  if (typeof role === "string" && validRoles.includes(role.toLowerCase() as UserRole)) {
    return role.toLowerCase() as UserRole;
  }

  return null;
}

// function parsePermissions(raw: unknown): PermissionMap {
//   if (!raw || typeof raw !== "object") return EMPTY_PERMISSIONS;
//   const map = raw as Record<string, unknown>;
//   return {
//     "shift.assign_any_interviewer": map["shift.assign_any_interviewer"] === true,
//     "shift.assign_any_assistant": map["shift.assign_any_assistant"] === true,
//     "shift.remove_any_interviewer": map["shift.remove_any_interviewer"] === true,
//     "shift.remove_any_assistant": map["shift.remove_any_assistant"] === true,
//   };
// }

// function parseStoredUser(rawValue: string | null): User | null {
//   if (!rawValue) {
//     return null;
//   }

//   try {
//     const parsed = JSON.parse(rawValue) as Partial<User> & {
//       roles?: unknown[];
//       first_name?: unknown;
//       last_name?: unknown;
//       role?: unknown;
//     };

//     if (typeof parsed.first_name !== "string" || typeof parsed.last_name !== "string" || !Array.isArray(parsed.roles)) {
//       return null;
//     }

//     const normalizedRoles = parsed.roles
//       .map((role) => normalizeRole(role))
//       .filter((role): role is UserRole => role !== null);

//     if (normalizedRoles.length === 0) {
//       return null;
//     }

//     return {
//       first_name: parsed.first_name ?? "",
//       last_name: parsed.last_name ?? "",
//       role: parsed.role,
//       roles: normalizedRoles
//     };
//   } catch {
//     return null;
//   }
// }

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  // const [user, setUser] = useState<User>(() => {
  //   if (typeof window === "undefined") {
  //     console.warn("Running in non-browser environment, using mock user");
  //     console.warn("Running in non-browser environment, using mock user");
  //     return mockUser;
  //   }

  //   return parseStoredUser(window.localStorage.getItem(AUTH_STORAGE_KEY)) ?? undefined;
  // });
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadAuthenticatedUser() {
      try {
        const userResponse = await getMe();
        const roleId =
          typeof userResponse.role === "string" ? userResponse.role : "";

        let roles: UserRole[] = [];

        if (roleId) {
          try {
            const roleResponse = await getRole(roleId);
            const normalizedRole = normalizeRole(roleResponse.name);

            if (normalizedRole) {
              roles = [normalizedRole];
            }
          } catch (roleError) {
            console.error("Error loading role:", roleError);
          }
        }

        const firstName =
          typeof userResponse.first_name === "string"
            ? userResponse.first_name.trim()
            : "";
        const lastName =
          typeof userResponse.last_name === "string"
            ? userResponse.last_name.trim()
            : "";
        const loadedUser: User = {
          id: typeof userResponse.id === "string" ? userResponse.id : "",
          role: roleId,
          cod_curso:
            typeof userResponse.cod_curso === "string"
              ? userResponse.cod_curso
              : undefined,
          cod_nivel:
            typeof userResponse.cod_nivel === "string"
              ? userResponse.cod_nivel
              : undefined,
          num_ordem:
            typeof userResponse.num_ordem === "number"
              ? userResponse.num_ordem
              : undefined,
          roles,
          first_name: firstName,
          last_name: lastName,
          permissions: EMPTY_PERMISSIONS
        };

        if (isMounted) {
          setUser(loadedUser);
        }
      } catch (error) {
        console.error("Error loading authenticated user:", error);

        if (isMounted) {
          setUser(undefined);
        }
      } finally {
        if (isMounted) {
          setIsAuthLoading(false);
        }
      }
    }

    loadAuthenticatedUser();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasPermission = useMemo(
    () => (key: PermissionKey): boolean => user?.permissions?.[key] === true,
    [user]
  );

  const contactInfoStale = useMemo(() => isContactInfoStale(user), [user]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      hasPermission,
      contactInfoStale,
      isAuthLoading,
      logout: async () => {
        try {
          await logoutApi();
          setUser(undefined);
        } catch (err) {
          console.error("logout error:", err);
        }
      }
    }),
    [isAuthLoading, user, hasPermission, contactInfoStale]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
